import { Component, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';

/**
 * Rendered for any in-app path that doesn't match a configured route (the
 * `**` route in `app-routing.module.ts`). Keeps unknown/stale links inside
 * the SPA instead of falling through to `LoginComponent`, whose
 * `ngOnInit()` unconditionally calls `AuthService.redirectToLogin()` — a
 * hard `window.location.href` bounce to the external QA login host that
 * wipes all SPA state (see task-6-report.md for the full trace).
 *
 * Markup mirrors the existing empty-state pattern used across list views
 * (icon + title + a primary-button CTA — see PlansListComponent's "No
 * plans yet" / "No plans match your filters" empty states,
 * plans-list.component.html:159-194), reusing only classes/tokens that
 * already exist: `.sp-card` / `.sp-btn-primary` (global, styles.scss) for
 * the card and CTA, CSS custom properties for everything else, and the
 * exact icon glyph already used for the plans-list "nothing here" state.
 * No new colors or spacing values are introduced.
 */
@Component({
  selector: 'sp-not-found',
  standalone: true,
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="nf">
      <div class="nf__card sp-card">
        <svg class="nf__icon" width="40" height="40" viewBox="0 0 40 40" fill="none">
          <rect x="6" y="8" width="28" height="26" rx="3" stroke="currentColor" stroke-width="1.8"/>
          <path d="M13 16h14M13 22h10M13 28h6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
        </svg>
        <p class="nf__title">Page not found</p>
        <p class="nf__text">The page you're looking for doesn't exist or may have been moved.</p>
        <a routerLink="/home" class="sp-btn-primary">Back to Home</a>
      </div>
    </div>
  `,
  styles: [`
    .nf {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--spacing-2xl);
    }
    .nf__card {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: var(--spacing-sm);
      max-width: 360px;
    }
    .nf__icon {
      color: var(--color-text-secondary);
      opacity: 0.5;
      margin-bottom: var(--spacing-sm);
    }
    .nf__title {
      font-size: var(--font-size-lg);
      font-weight: 700;
      color: var(--color-text-primary);
      margin: 0;
    }
    .nf__text {
      font-size: var(--font-size-base);
      color: var(--color-text-secondary);
      margin: 0 0 var(--spacing-sm);
    }
  `],
})
export class NotFoundComponent {}
